<template>
  <Modal
    event-tracking-source="add team members popup"
    class="add-additional-members-modal"
    :hasClose="true"
    @close="$emit('close')"
  >
    <form @submit.prevent="submitForm">
      <h2
        slot="header"
        class="text-ma-black-1 text-center text-xl m-3 font-semibold"
      >
        Add Additional Members
      </h2>
      <p
        class="m-auto w-full text-ma-gray-3 text-base mb-4 text-center max-w-md mb-1"
      >
        Enter email address separated by comma to invite team members.
      </p>
      <Message v-if="errorMessage" :message="errorMessage" level="error" class="mb-3" />
      <vue-tags-input
        placeholder="|Enter email address"
        class=""
        v-model="tag"
        :tags="inviteEmails"
        :disabled="submitting"
        :avoid-adding-duplicates="false"
        @tags-changed="onTagChanged"
        @before-adding-tag="onAddingInviteEmail"
      />
      <div v-if="inviteEmailsError" class="text-ma-red-1 text-base">
          {{ inviteEmailsError | MESSAGE }}
      </div>
      <div class="flex justify-between w-full mt-3">
        <div class="text-left">
          <div class="text-ma-black-1 text-xl font-semibold text-left mb-3">
            Prorated Total
          </div>
          <div class="text-ma-black-1 text-xl font-semibold text-left mb-3" v-if="addtionalMembers.tax_rate">
            VAT ({{addtionalMembers.tax_rate}}%)
          </div>
          <div class="text-ma-black-1 text-xl font-semibold text-left mb-3" v-if="addtionalMembers.tax_rate">
            TOTAL
          </div>
          <div class="text-ma-gray-3 text-xs font-bold text-left uppercase">
            <template v-if="emptySeats">
              {{ emptySeats }} EMPTY SEAT<template v-if="emptySeats > 1">S</template>
            </template>
            <template v-if="emptySeats && countAdditionalMembers">
              &nbsp;+&nbsp;
            </template>
            <template v-if="countAdditionalMembers">
              {{ countAdditionalMembers }} ADDITIONAL MEMBER<template v-if="countAdditionalMembers > 1">S</template>
            </template>
          </div>
        </div>
        <div class="text-right">
          <div
            v-if="countNewMembers"
            class="text-ma-black-1 text-xl font-semibold text-right mb-3 uppercase"
          >
            <template v-if="calculating">
              Calculating...
            </template>
            <template v-else>
              {{ addtionalMembers.prorated_amount / 100 | CURRENCY }} Per Year
            </template>
          </div>
          <div
            v-if="addtionalMembers.tax_rate"
            class="text-ma-black-1 text-xl font-semibold text-right mb-3 uppercase"
          >
            <template v-if="calculating">
              Calculating...
            </template>
            <template v-else>
              {{ (addtionalMembers.tax_amount / 100) | CURRENCY }}
            </template>
          </div>
          <div
            v-if="addtionalMembers.tax_rate"
            class="text-ma-black-1 text-xl font-semibold text-right mb-3 uppercase"
          >
            <template v-if="calculating">
              Calculating...
            </template>
            <template v-else>
              {{ (addtionalMembers.prorated_amount / 100) + (addtionalMembers.tax_amount / 100) | CURRENCY }}
            </template>
          </div>
          <div v-if="countAdditionalMembers" class="text-ma-gray-3 text-xs font-bold text-right uppercase">
            <template v-if="calculating">
              ...
            </template>
            <template v-else>
              <!-- DISCOUNT 10% -->
              {{ countAdditionalMembers }} x {{ addtionalMembers.prorated_amount / .9 / countAdditionalMembers / 100 | CURRENCY }} - (10% discount)
            </template>
          </div>
        </div>
      </div>

      <div v-if="addtionalMembers.days_until_renewal" class="bg-ma-gray-0 py-3 px-4 text-ma-black-1 text-sm mt-5 mb-3">
        There are {{addtionalMembers.days_until_renewal}} day<template v-if="addtionalMembers.days_until_renewal > 1">s</template> left until renewal. All additional members will be
        charged for that period. On the start of the new billing cycle, all
        members will be charged for a full year.
      </div>

      <p
        class="m-auto w-full text-ma-gray-3 text-base mb-4 text-center max-w-md pt-5"
      >
        Confirm below to add new members and complete payment.
      </p>

      <div slot="footer">
        <div class="mx-auto mb-3 flex justify-center w-full max-w-md">
          <button
            type="button"
            @click="$emit('close')"
            class="cursor-pointer inline-block w-32 mr-4 md:w-40 font-semibold text-center btn btn--purple-outline text-base py-2"
          >
            Cancel
          </button>
          <button
            :disabled="submitting || calculating || !countNewMembers"
            type="submit"
            class="btn btn--primary w-32 md:w-40 truncate"
          >
            <template v-if="submitting">
              <div class="animation-spin btn-spinner inline-block ml-1">
                <i class="icon--spinner2"></i>
              </div>
              Confirming ...
            </template>
            <template v-else-if="calculating">
              <div class="animation-spin btn-spinner inline-block ml-1">
                <i class="icon--spinner2"></i>
              </div>
              Calculating ...
            </template>
            <template v-else>
              {{ addtionalMembers.prorated_amount ? 'Confirm & Pay' : 'Confirm' }}
            </template>
          </button>
        </div>
      </div>
    </form>
  </Modal>
</template>

<script>
/* constants/utils */
import { SEAT_PRICE } from "@/constants";
import { isValidEmail } from "@/utils";

/* components */
import Modal from "@/components/Modal";
import Message from "@/components/Message";

/* vuex */
import { mapState } from "vuex";

export default {
  components: {
    Modal,
    Message,
  },
  props: {},
  data: () => ({
    /* constants */
    seatPrice: SEAT_PRICE,

    /* main */
    tag: "",
    calculating: false,
  }),
  computed: {
    ...mapState({
      currentUser: (state) => state.user,
      addtionalMembers: (state) => state.teamManagement.addtionalMembers,
      members: (state) => state.teamManagement.members,
      myTeam: (state) => state.teamManagement.myTeam,
      submitting: (state) => state.teamManagement.loading,
      error: (state) => state.teamManagement.error,
    }),
    emptySeats() {
      return this.myTeam && this.myTeam.seats && this.myTeam.seats.empty;
    },
    totalSeats() {
      return this.myTeam && this.myTeam.seats && this.myTeam.seats.total;
    },
    countNewMembers() {
      return (!this.inviteEmails || !this.inviteEmails.length) ? 0 : this.inviteEmails.filter(e => !e.classes).length;
    },
    quantity() {
      return Math.max(this.totalSeats, this.totalSeats - this.emptySeats + this.countNewMembers);
    },
    countAdditionalMembers() {
      const count = this.countNewMembers - this.emptySeats;
      return count > 0 ? count : 0;
    },
    inviteEmails: {
      get() {
        return this.addtionalMembers.invite_emails.map((email, index) => {
          return {
            text: email,
            classes: !isValidEmail(email)
              ? "ti-invalid"
              : email === (this.currentUser && this.currentUser.email)
              ? "ti-owners"
              : this.addtionalMembers.invite_emails.indexOf(email) < index
              ? "ti-duplicate"
              : "",
          };
        });
      },
      set(items) {
        this.$store.dispatch("teamManagement/setAdditionalMembers", {
          invite_emails: items.map((item) => item.text.trim()),
        });
      },
    },
    inviteEmailsError() {
      return this.error &&
        this.error.response &&
        this.error.response.data &&
        this.error.response.data.errors &&
        this.error.response.data.errors.invite_emails &&
        this.error.response.data.errors.invite_emails[0];
    },
    errorMessage() {
      return this.error &&
        this.error.response &&
        this.error.response.data &&
          (this.error.response.data.message ||this.error.response.data.error);
    }
  },
  watch: {
    tag(str) {
      const lastLetter = str && str.length && str.charAt(str.length - 1);
      if (lastLetter && " ,;".includes(lastLetter)) {
        const lastLetter = str.charAt(str.length - 1);
        this.onAddingInviteEmail({
          tag: {
            text: str.substr(0, str.length - 1),
          },
        });
        this.tag = "";
      }
    },
  },
  mounted() {
    this.inviteEmails = [];
    this.calculateProrateAmmount();
  },
  methods: {
    async submitForm() {
      const { invite_emails, proration_date } = this.addtionalMembers;
      if (!this.countNewMembers) {
        this.$store.dispatch("teamManagement/setError", "Must include at least 1 email");
        return;
      }
      const data = (this.addtionalMembers.prorated_amount) ?
        await this.$store.dispatch(
          "teamManagement/updateSeats",
          {
            quantity: this.quantity,
            proration_date,
            invite_emails,
          }
        ) :
        await this.$store.dispatch(
          "teamManagement/inviteMembers",
          {
            invite_emails,
          }
        );
      if (data) {
        this.$emit("updated");
      }
    },
    async calculateProrateAmmount() {
      try {
        this.calculating = true;
        this.$store.dispatch("teamManagement/setError", null);
        await this.$store.dispatch(
          "teamManagement/calculateProrateAmmount",
          {
            quantity: this.quantity,
          }
        );
        this.calculating = false;
      } catch (error) {
        this.calculating = false;
      }
    },
    onTagChanged(newTags) {
      this.inviteEmails = newTags;
      this.calculateProrateAmmount();
    },
    onAddingInviteEmail(obj) {
      const emails = obj.tag.text.split(/[ ,;]+/).filter((e) => !!e);
      this.inviteEmails = this.inviteEmails.concat(
        emails.map((email) => {
          return {
            text: email.toLowerCase(),
          };
        })
      );
      this.tag = "";
      this.calculateProrateAmmount();
    },
  },
};
</script>

<style>
.add-additional-members-modal {
  overflow: auto;
  .modal-container {
    width: 900px;
    margin-top: auto;
    margin-bottom: auto;
    padding: 20px;
    .modal-body {
      max-width: 670px;
      margin-left: auto;
      margin-right: auto;
    }
    @media (max-width: 768px) {
      width: 100vw !important;
      height: 100%;
      margin-top: 0px;
      overflow-y: auto;
    }
  }
  .vue-tags-input {
    max-width: 100% !important;
    .ti-new-tag-input-wrapper {
      min-height: 30px !important;
    }
  }
}
</style>
